/**
 * RefundsSection — "Request a Refund" (LEVEL_12_REFUND_AND_CANCELLATION.md
 * §18.1). Lists the user's completed purchases; each can have at most one
 * open refund request. The user only ever creates a REQUEST here — never a
 * "Refund via Razorpay" action, and never anything that looks like they can
 * execute the refund themselves. Every status shown is read-only, driven by
 * the backend's own RefundRequest/Refund records.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Icons } from '@/src/modules/components/Icons';
import { Button } from '@/src/modules/components/Button';
import { SettingsCard, Pill } from '@/src/modules/components/settings/primitives';
import { useToast } from '@/src/modules/components/settings/ToastProvider';
import {
  cancelRefundRequest,
  createRefundRequest,
  getBillingCapabilities,
  listPurchases,
  listRefundRequests,
} from '@/src/modules/purchase/api';
import type { PurchaseListItem, RefundCategory, RefundRequest } from '@/src/modules/purchase/types';

function mintIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatPaise(paise: number, currency: string) {
  return `${currency} ${(paise / 100).toFixed(2)}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_TONE: Record<RefundRequest['status'], 'primary' | 'success' | 'neutral' | 'warning'> = {
  PENDING_REVIEW: 'primary',
  APPROVED: 'success',
  REJECTED: 'warning',
  CANCELLED: 'neutral',
};

const STATUS_LABEL: Record<RefundRequest['status'], string> = {
  PENDING_REVIEW: 'Refund requested — pending review',
  APPROVED: 'Refund approved — processing',
  REJECTED: 'Refund request declined',
  CANCELLED: 'Refund request cancelled',
};

export function RefundsSection() {
  const { toast } = useToast();
  const [purchases, setPurchases] = useState<PurchaseListItem[]>([]);
  const [requestsByPurchase, setRequestsByPurchase] = useState<Record<string, RefundRequest[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openFormFor, setOpenFormFor] = useState<string | null>(null);
  const [refundExecutionAvailable, setRefundExecutionAvailable] = useState(false);

  const [statusUnknownFor, setStatusUnknownFor] = useState<Set<string>>(new Set());
  const [retryingFor, setRetryingFor] = useState<Set<string>>(new Set());
  const [cancellingFor, setCancellingFor] = useState<Set<string>>(new Set());

  // Small, bounded (order history is typically short); each purchase's own
  // request list is fetched independently since there is no cross-purchase
  // "all my refund requests" endpoint (§19.1's API surface is deliberately
  // scoped per-purchase). A fetch failure is tracked explicitly
  // (statusUnknownFor) rather than silently defaulting to "no open
  // request" — that default would render an enabled "Request refund"
  // button for a purchase that may already have one, guaranteeing a 409 on
  // submit (caught in review). Extracted as its own function (not just
  // inline in the mount effect) so a per-purchase "Retry" action — round-2
  // review: a failed fetch previously had no recovery path short of
  // leaving and re-entering the whole section — can re-run it for just one
  // purchase without refetching the entire list.
  async function refreshRequestsFor(purchaseIds: string[]) {
    const results = await Promise.all(
      purchaseIds.map(async (id) => {
        try {
          const r = await listRefundRequests(id);
          return { id, items: r.items, failed: false } as const;
        } catch {
          return { id, items: [] as RefundRequest[], failed: true } as const;
        }
      }),
    );
    setRequestsByPurchase((prev) => {
      const next = { ...prev };
      for (const r of results) if (!r.failed) next[r.id] = r.items;
      return next;
    });
    setStatusUnknownFor((prev) => {
      const next = new Set(prev);
      for (const r of results) (r.failed ? next.add(r.id) : next.delete(r.id));
      return next;
    });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([listPurchases(), getBillingCapabilities()])
      .then(async ([res, caps]) => {
        if (cancelled) return;
        setRefundExecutionAvailable(caps.refundExecutionAvailable);
        const eligible = res.items.filter(
          (p) =>
            p.status === 'COMPLETED' ||
            Boolean(p.walletCredit) ||
            Boolean(p.hasRefundedPayment) ||
            (p.refundedAmountPaise !== undefined && p.refundedAmountPaise > 0),
        );
        setPurchases(eligible);
        await refreshRequestsFor(eligible.map((p) => p.id));
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Could not load your purchases'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function retryStatusFor(purchaseId: string) {
    setRetryingFor((prev) => new Set(prev).add(purchaseId));
    await refreshRequestsFor([purchaseId]);
    setRetryingFor((prev) => {
      const next = new Set(prev);
      next.delete(purchaseId);
      return next;
    });
  }

  async function handleCancelRequest(purchaseId: string, requestId: string) {
    setCancellingFor((prev) => new Set(prev).add(requestId));
    try {
      await cancelRefundRequest(purchaseId, requestId);
      await refreshRequestsFor([purchaseId]);
      toast('Refund request cancelled.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not cancel refund request', 'error');
    } finally {
      setCancellingFor((prev) => {
        const next = new Set(prev);
        next.delete(requestId);
        return next;
      });
    }
  }

  async function submitRequest(purchaseId: string, reason: string, category: RefundCategory) {
    if (!refundExecutionAvailable) {
      toast('Refund requests are temporarily unavailable. Please try again later.', 'error');
      return;
    }
    try {
      const created = await createRefundRequest(purchaseId, { reason, category }, mintIdempotencyKey());
      setRequestsByPurchase((prev) => ({
        ...prev,
        [purchaseId]: [created, ...(prev[purchaseId] ?? [])],
      }));
      setOpenFormFor(null);
      toast('Refund request submitted — an admin will review it.', 'success');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not submit refund request', 'error');
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <SettingsCard
        icon={Icons.RotateCcw}
        title="Refunds"
        description="Request a refund for a completed purchase. An admin reviews every request before any money moves."
      >
        {!loading && !refundExecutionAvailable && (
          <p
            role="status"
            className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-on-surface"
          >
            Refund requests are temporarily unavailable while billing operations are offline. You
            can still view past requests below; new submissions are disabled until service resumes.
          </p>
        )}
        {loading && <p className="text-sm text-on-surface-variant">Loading your purchases…</p>}
        {error && <p className="text-sm text-status-error">{error}</p>}
        {!loading && !error && purchases.length === 0 && (
          <div className="flex items-center gap-4 rounded-2xl border border-dashed border-outline-variant/50 bg-surface-low/50 p-4">
            <div className="w-12 h-8 rounded-md bg-surface-lowest border border-outline-variant/40 flex items-center justify-center text-on-surface-variant flex-shrink-0">
              <Icons.RotateCcw size={16} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-on-surface">No completed purchases yet</p>
              <p className="text-xs text-on-surface-variant">Refund requests appear here once you have a completed purchase.</p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {purchases.map((p) => {
            const requests = requestsByPurchase[p.id] ?? [];
            // APPROVED blocks a new request UNLESS the linked execution
            // Refund actually FAILED at the gateway — RefundRequest.status
            // itself never leaves APPROVED once set (it's terminal), so
            // without checking refundStatus a failed refund would leave the
            // user permanently unable to ever request again (caught in
            // review — see the `refundStatus` field's own doc comment).
            const openRequest = requests.find(
              (r) =>
                r.status === 'PENDING_REVIEW' ||
                (r.status === 'APPROVED' && r.refundStatus !== 'FAILED'),
            );
            const latestResolved = requests.find(
              (r) =>
                r.status === 'REJECTED' ||
                r.status === 'CANCELLED' ||
                (r.status === 'APPROVED' && r.refundStatus === 'FAILED'),
            );
            const isFullyRefunded =
              (p.refundedAmountPaise !== undefined && p.refundedAmountPaise >= p.amount.totalPaise) ||
              (p.status === 'EXPIRED' && Boolean(p.hasRefundedPayment));
            const statusUnknown = statusUnknownFor.has(p.id);
            const canRequest = refundExecutionAvailable && !openRequest && !statusUnknown && !isFullyRefunded;

            return (
              <div key={p.id} className="rounded-2xl bg-surface-low p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-on-surface">{p.orderNumber}</p>
                    <p className="text-xs text-on-surface-variant">
                      {formatPaise(p.amount.totalPaise, p.amount.currency)} · {formatDate(p.createdAt)}
                      {(p.hasRefundedPayment || (p.refundedAmountPaise !== undefined && p.refundedAmountPaise > 0)) && ' · Refunded'}
                    </p>
                  </div>
                  {statusUnknown ? (
                    <div className="flex items-center gap-2">
                      <Pill tone="warning">Couldn’t check refund status</Pill>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={retryingFor.has(p.id)}
                        onClick={() => void retryStatusFor(p.id)}
                      >
                        {retryingFor.has(p.id) ? 'Retrying…' : 'Retry'}
                      </Button>
                    </div>
                  ) : isFullyRefunded ? (
                    <Pill tone="neutral">Refunded</Pill>
                  ) : canRequest ? (
                    <Button variant="outline" size="sm" onClick={() => setOpenFormFor(p.id)}>
                      Request refund
                    </Button>
                  ) : openRequest ? (
                    <div className="flex items-center gap-2">
                      <Pill tone={STATUS_TONE[openRequest.status]}>{STATUS_LABEL[openRequest.status]}</Pill>
                      {openRequest.status === 'PENDING_REVIEW' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={cancellingFor.has(openRequest.id)}
                          onClick={() => void handleCancelRequest(p.id, openRequest.id)}
                        >
                          {cancellingFor.has(openRequest.id) ? 'Cancelling…' : 'Cancel request'}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Pill tone="neutral">Unavailable</Pill>
                  )}
                </div>

                {latestResolved && !openRequest && (
                  <div className="mt-2 text-xs text-on-surface-variant">
                    Previous request:{' '}
                    {latestResolved.status === 'APPROVED'
                      ? 'Refund failed — you can submit a new request'
                      : STATUS_LABEL[latestResolved.status]}
                    {latestResolved.decisionReason ? ` — ${latestResolved.decisionReason}` : ''}
                  </div>
                )}

                <AnimatePresence>
                  {openFormFor === p.id && (
                    <RefundRequestForm
                      isPack={Boolean(p.isCreditPack || p.creditPackId)}
                      defaultCategory={
                        p.walletCredit
                          ? 'PAYMENT_ISSUE'
                          : Boolean(p.isCreditPack || p.creditPackId)
                            ? 'PACK_REFUND'
                            : 'SERVICE_FAULT'
                      }
                      onCancel={() => setOpenFormFor(null)}
                      onSubmit={(reason, category) => submitRequest(p.id, reason, category)}
                    />
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </SettingsCard>
    </div>
  );
}

function RefundRequestForm({
  defaultCategory = 'SERVICE_FAULT',
  isPack = false,
  onSubmit,
  onCancel,
}: {
  defaultCategory?: RefundCategory;
  isPack?: boolean;
  onSubmit: (reason: string, category: RefundCategory) => Promise<void>;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState<RefundCategory>(defaultCategory);
  const [submitting, setSubmitting] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-3 space-y-2 overflow-hidden"
    >
      <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
        What went wrong?
      </label>
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value as RefundCategory)}
        className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-outline-variant/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      >
        {isPack ? (
          <>
            <option value="PACK_REFUND">Pack refund — unused credits (refund = paid − credits used, within 7 days)</option>
            <option value="PAYMENT_ISSUE">Payment problem — duplicate, unauthorized or failed charge (report within 3 working days)</option>
          </>
        ) : (
          <>
            <option value="SERVICE_FAULT">Problem with the invitation or template (only where we have detected one; you then have 24 hours)</option>
            <option value="PAYMENT_ISSUE">Payment problem — duplicate, unauthorized or failed charge (report within 3 working days)</option>
            <option value="PACK_REFUND">Pack refund — unused credits (within 7 days)</option>
          </>
        )}
      </select>
      <label className="block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">
        Why are you requesting a refund?
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="Tell us what happened (min 3 characters)"
        className="w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-outline-variant/40 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={reason.trim().length < 3 || submitting}
          onClick={async () => {
            setSubmitting(true);
            await onSubmit(reason.trim(), category);
            setSubmitting(false);
          }}
        >
          {submitting ? 'Submitting…' : 'Submit request'}
        </Button>
      </div>
    </motion.div>
  );
}
